import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axioInstance from '../components/axiosInstance';
import SweetAlert from 'sweetalert2';
import { Encryption } from '../components/global';
import NewTheme from '../components/admin-dashboard/NewTheme';
import TableContainer from '../components/admin-dashboard/TableContainer';

export default function PlanBulkUpdate() {
  let navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [data, setData] = useState([]);
  const [tableRowsData, setTableRowsData] = useState([]);
  const [loading, setLoading] = useState(false);

  // States for source filters (Source managers to search for)
  const [relManagers, setRelManagers] = useState([]);
  const [serManagers, setSerManagers] = useState([]);
  const [selectedSourceRel, setSelectedSourceRel] = useState("");
  const [selectedSourceSer, setSelectedSourceSer] = useState("");

  // States for target managers (New managers to assign)
  const [targetRel, setTargetRel] = useState("");
  const [targetSer, setTargetSer] = useState("");

  // Checkbox management
  const [selectedRows, setSelectedRows] = useState([]);

  useEffect(() => {
    const loggedInUser = sessionStorage.getItem("admin_id");
    if (loggedInUser === null) {
      navigate("/ulogin");
      return;
    }

    // Fetch all Admin Users and filter by department for RM and Service Manager dropdowns
    axioInstance.get(`admin-users?status=`).then(response => {
      if (response.data.status === 'success') {
        const allUsers = response.data.data || [];

        // RM Department Logic: 11, 13, 14
        const rms = allUsers.filter(u => ['11', '13', '14'].includes(String(u.department)));
        setRelManagers(rms.map(u => ({ emp_id: u.id, short_name: u.short_name, emp_name: u.admin_name })));

        // Service Manager Department Logic: 13, 14
        const sms = allUsers.filter(u => ['13', '14'].includes(String(u.department)));
        setSerManagers(sms.map(u => ({ emp_id: u.id, short_name: u.short_name, emp_name: u.admin_name })));
      }
    });

    // Initial fetch if desired, or keep it empty until search
  }, [navigate]);

  const fetchProfiles = () => {
    setLoading(true);
    const params = {
      selected_rel_manager: selectedSourceRel,
      selected_ser_manager: selectedSourceSer,
      admin_role: '0'
    };

    axioInstance.post(`admin/get-all-profiles`, params).then(response => {
      if (response.data.status === 100) {
        setData(response.data.data);
        setTableRowsData(response.data.data);
        setSelectedRows([]); // Clear selections on new search
      } else {
        setData([]);
        setTableRowsData([]);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const resetFilter = () => {
    setSelectedSourceRel("");
    setSelectedSourceSer("");
    setTableRowsData([]);
    setData([]);
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(tableRowsData.map(row => row.profile_id));
    } else {
      setSelectedRows([]);
    }
  };

  const toggleSelectRow = (e, profileId) => {
    if (e.target.checked) {
      setSelectedRows([...selectedRows, profileId]);
    } else {
      setSelectedRows(selectedRows.filter(id => id !== profileId));
    }
  };

  const handleBulkUpdate = () => {
    if (selectedRows.length === 0) {
      SweetAlert.fire('Error', 'Please select at least one plan', 'error');
      return;
    }
    if (!targetRel && !targetSer) {
      SweetAlert.fire('Error', 'Please select a new RM or Service Manager', 'error');
      return;
    }

    SweetAlert.fire({
      title: 'Are you sure?',
      text: `Update managers for ${selectedRows.length} selected plans?`,
      showCancelButton: true,
      confirmButtonText: 'Yes, Update!',
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          profile_ids: selectedRows.map(id => Encryption(id)),
          rel_manager: targetRel,
          ser_manager: targetSer
        };

        axioInstance.post(`admin/bulk-update-profile-managers`, payload).then(response => {
          if (response.data.status === 100) {
            SweetAlert.fire('Success', 'Managers updated successfully', 'success');
            setSelectedRows([]);
            fetchProfiles(); // Refresh list
          } else {
            SweetAlert.fire('Error', response.data.msg || 'Update failed', 'error');
          }
        });
      }
    });
  };

  // Re-apply datatable functionality if needed
  useEffect(() => {
    if (tableRowsData.length > 0) {
      setTimeout(() => {
        if (typeof window.applyDataTable === "function") {
          window.applyDataTable('#datatable_bulk_update', {
            "bFilter": true,
            "order": [[1, 'desc']]
          });
        }
      }, 500);
    }
  }, [tableRowsData]);

  return (
    <NewTheme loading={loading}>
      <div className="page-header">
        <div className="content-page-header">
          <h5>Plan Bulk Update</h5>
          <div className="list-btn">
            <ul className="filter-list">
              <li className='me-2'>
                <button type="button" className="btn btn-primary btn-block btnFinno" onClick={() => { navigate('/manage-admin-users') }}> Manage Admin User </button>
              </li>
              <li className='me-2'>
                <button type="button" className="btn btn-success btn-block btnFinno" onClick={() => { navigate('/admin-dashboard') }}> Admin Dashboard </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header">
          <h6 className="font16 fw600 color384 mb-0"><i className="fas fa-search me-2"></i>Find Plans to Update</h6>
        </div>
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-4">
              <label className="font14 color212 fw600">Source Relationship Manager</label>
              <select className="form-control" value={selectedSourceRel} onChange={(e) => setSelectedSourceRel(e.target.value)}>
                <option value="">-- All --</option>
                <option value="No Primary">No Primary RM</option>
                {relManagers.map(m => <option key={m.emp_id} value={m.short_name}>{m.emp_name}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label className="font14 color212 fw600">Source Service Manager</label>
              <select className="form-control" value={selectedSourceSer} onChange={(e) => setSelectedSourceSer(e.target.value)}>
                <option value="">-- All --</option>
                <option value="No Planner">No Service Manager</option>
                {serManagers.map(m => <option key={m.emp_id} value={m.short_name}>{m.emp_name}</option>)}
              </select>
            </div>
            <div className="col-md-4 d-flex">
              <button type="button" className="btn btn-primary me-2 flex-grow-1" onClick={fetchProfiles}>
                Search Plans
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={resetFilter}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {tableRowsData.length > 0 && (
        <div className="card mb-4" style={{ border: '1px solid #7367f033', backgroundColor: '#f8f7ff' }}>
          <div className="card-body">
            <div className='row align-items-end'>
              <div className='col-md-12 mb-3'>
                <h6 className="font16 fw600 color384"><i className="fas fa-layer-group me-2"></i>Bulk Action: Mapping Selected Plans ({selectedRows.length})</h6>
                <p className="text-muted font12 mb-0">Select the new managers below and click update to apply changes to all selected rows.</p>
              </div>
              <div className='col-md-3'>
                <label className="font14 color212 fw600">New RM</label>
                <select className="form-control" value={targetRel} onChange={(e) => setTargetRel(e.target.value)}>
                  <option value="">-- No Change --</option>
                  {relManagers.map(m => <option key={m.emp_id} value={m.short_name}>{m.emp_name}</option>)}
                </select>
              </div>
              <div className='col-md-3'>
                <label className="font14 color212 fw600">New Service Manager</label>
                <select className="form-control" value={targetSer} onChange={(e) => setTargetSer(e.target.value)}>
                  <option value="">-- No Change --</option>
                  {serManagers.map(m => <option key={m.emp_id} value={m.short_name}>{m.emp_name}</option>)}
                </select>
              </div>
              <div className='col-md-2'>
                <button className="btn btn-success btn-block w-100 mt-md-0 mt-3" onClick={handleBulkUpdate}>Update Selected</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <TableContainer>
        <table className="table table-stripped table-hover" id="datatable_bulk_update">
          <thead className="thead-light">
            <tr>
              <th>
                <input type="checkbox" onChange={toggleSelectAll}
                  checked={selectedRows.length === tableRowsData.length && tableRowsData.length > 0}
                />
              </th>
              <th>Profile ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Current RM</th>
              <th>Current Ser Manager</th>
            </tr>
          </thead>
          <tbody>
            {tableRowsData && tableRowsData.map((row, index) => (
              <tr key={index}>
                <td>
                  <input type="checkbox"
                    onChange={(e) => toggleSelectRow(e, row.profile_id)}
                    checked={selectedRows.includes(row.profile_id)}
                  />
                </td>
                <td>{row.profile_id}</td>
                <td>{row.name}</td>
                <td>{row.email}</td>
                <td>{row.primary_rel_manager}</td>
                <td>{row.plan_ser_manager}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableContainer>
    </NewTheme>
  );
}
